import type { FabricEditor } from 'modules/fabric-editor';
import {
  createLogoGroup,
  DEFAULT_LOGO_MARGIN,
  DEFAULT_LOGO_SCALE,
  DEFAULT_LOGO_SLOT,
  findLogo,
  normalizeLogoGroup,
  preloadLogoAssets,
} from 'modules/manager/domain/logo';

/**
 * Guarantees the mandatory ImGame logo is present and on top of the exported
 * image. Idempotent and self-contained (does not depend on the logo plugin
 * being installed), so it also covers legacy templates whose stored JSON
 * predates the feature and render pages with a leaner plugin set. Runs last in
 * the pipeline so it sits above the substituted content.
 */
export async function ensureMandatoryLogo(editor: FabricEditor): Promise<void> {
  const canvas = editor.canvas;
  const existing = findLogo(canvas);
  if (existing) {
    // Stored JSON may carry a logo that was draggable in the editor before the
    // lock was enforced on load — re-anchor it to its `data.position` slot so
    // the exported image matches what the inspector says.
    normalizeLogoGroup(existing, editor.resolution);
    canvas.bringToFront(existing);
    canvas.requestRenderAll();
    return;
  }
  // Guarantees the raster cache is warm before this cold-editor invocation of
  // `createLogoGroup`, which reads it synchronously (see logo-factory.ts).
  await preloadLogoAssets();
  const group = createLogoGroup(DEFAULT_LOGO_SLOT, DEFAULT_LOGO_SCALE, DEFAULT_LOGO_MARGIN, editor.resolution);
  canvas.add(group);
  canvas.bringToFront(group);
  canvas.requestRenderAll();
}
